import * as settings from "middleware/settings";
import { replace } from "react-router-redux";
import { actions as notificationActions } from "reducers/notification";
import { types as pushNotificationsTypes } from "reducers/pushNotifications";
import { call, put, select, takeLatest } from "redux-saga/effects";
import * as deviceUtils from "util/device";

import * as session from "middleware/session";
import { selectors } from "reducers/login";
import { selectors as sessionSelectors } from "reducers/session";
import * as i18nUtils from "util/i18n";
import * as pushNotificationsUtils from "util/pushNotifications";
import { isCurrentDeviceRegistered, withTimeout } from "util/pushNotifications";

const sagas = [
    takeLatest(pushNotificationsTypes.PUSH_NOTIFICATIONS_LIST_DEVICES, listDevices),
    takeLatest(pushNotificationsTypes.PUSH_NOTIFICATIONS_DELETE_DEVICE, deleteDevice),
    takeLatest(pushNotificationsTypes.PUSH_NOTIFICATIONS_REGISTER_DEVICE, registerDevice),
    takeLatest(pushNotificationsTypes.LIST_ALL_USER_DEVICES, listAllUserDevices),
    takeLatest(pushNotificationsTypes.ASK_PERMISIONS, pushNotificationsAskPermission),
    takeLatest(pushNotificationsTypes.LIST_USER_DEVICES_CONFIGURED, listUserDevicesConfigured),
    takeLatest(pushNotificationsTypes.CHECK_CONFIGURATION, checkConfiguration),
    takeLatest(pushNotificationsTypes.LINK_DEVICE, handleLinkDeviceSaga),
    takeLatest(pushNotificationsTypes.PUSH_NOTIFICATION_REFRESH_TOKEN, handleRefreshTokenPush),
];

export default sagas;

function* registerDevice() {
    const isEnabledPlugin = yield call(pushNotificationsUtils.isPushEnabled);
    const enabled = isEnabledPlugin?.enabled;
    if (enabled) {
        const response = yield call(pushNotificationsUtils.getTokenPush);
        const registrationId = response?.registrationId;
        yield call(linkDevice, registrationId);
    } else {
        yield call(pushNotificationsUtils.askPermission);
        try {
            yield call(pushNotificationsUtils.waitForEnabled);
        } catch {
            yield put({
                type: pushNotificationsTypes.SET_FETCHING_ACTION,
                fetching: false,
            });
            const errorMessage = i18nUtils.get("settings.pushNotifications.device.nopermissions.warning");
            yield put(notificationActions.showNotification(errorMessage, "error", ["pushDevices"]));
            return;
        }
        try {
            const responseToken = yield withTimeout(pushNotificationsUtils.getTokenPush());
            const registrationId = responseToken?.registrationId;
            yield call(linkDevice, registrationId);
        } catch {
            yield put({
                type: pushNotificationsTypes.SET_FETCHING_ACTION,
                fetching: false,
            });
            const errorMessage = i18nUtils.get("settings.pushNotifications.device.register.error");
            yield put(notificationActions.showNotification(errorMessage, "error", ["pushDevices"]));
        }
    }
}

function* handleLinkDeviceSaga(action) {
    try {
        const { registrationId } = action.payload;
        yield call(linkDevice, registrationId);
    } finally {
        yield put({
            type: pushNotificationsTypes.SET_FETCHING,
            fetching: false,
        });
    }
}

function* linkDevice(registrationId) {
    try {
        const extraInfo = window.device;
        extraInfo.uuid = window.app.getDeviceUUID();
        const response = yield call(
            settings.pushNotificationsRegisterUserDevice,
            window.app.getDeviceUUID(),
            registrationId,
            JSON.stringify(extraInfo),
        );

        if (response && response.type === "I") {
            const confirmationMessage = i18nUtils.get("settings.pushNotifications.device.register.success");
            yield put(notificationActions.showNotification(confirmationMessage, "success", ["pushDevices"]));
            yield put({
                type: pushNotificationsTypes.LIST_USER_DEVICES_CONFIGURED,
            });
        } else {
            const errorMessage = i18nUtils.get("settings.pushNotifications.device.register.error");
            yield put(notificationActions.showNotification(errorMessage, "error", ["pushDevices"]));
        }
    } finally {
        yield put({
            type: pushNotificationsTypes.SET_FETCHING_ACTION,
            fetching: false,
        });
    }
}

function* deleteDevice({ idDevice }) {
    try {
        const response = yield call(settings.pushNotificationsDeleteUserDevice, idDevice);

        if (response && response.type === "I") {
            yield call(pushNotificationsUtils.firebaseUnregister);
            const confirmationMessage = i18nUtils.get("settings.pushNotifications.device.remove.success");
            yield put(notificationActions.showNotification(confirmationMessage, "success", ["pushDevices"]));
            yield put({
                type: pushNotificationsTypes.LIST_USER_DEVICES_CONFIGURED,
            });
        } else {
            const errorMessage = i18nUtils.get("settings.pushNotifications.device.remove.error");
            yield put(notificationActions.showNotification(errorMessage, "error", ["pushDevices"]));
        }
    } finally {
        yield put({
            type: pushNotificationsTypes.SET_FETCHING,
            fetching: false,
        });
    }
}

function* listDevices() {
    const response = yield call(settings.pushNotificationsListUserDevices);
    if (response && response.type === "I") {
        yield put({
            type: pushNotificationsTypes.PUSH_NOTIFICATIONS_LIST_DEVICES_SUCCESS,
            listUserDevices: response.data.data.listUserDevices,
        });
    } else {
        yield put({
            type: pushNotificationsTypes.CLEAN,
        });
        yield put(notificationActions.showNotification(response.data.message, "error", ["settings"]));
        yield put(replace("/settings"));
    }
}

function* listAllUserDevices() {
    const response = yield call(settings.listAllUserDevices);

    if (response && response.type === "I") {
        yield put({
            type: pushNotificationsTypes.PUSH_NOTIFICATIONS_LIST_DEVICES_SUCCESS,
            listUserDevices: response.data.data.listUserDevices,
        });
    } else {
        yield put({
            type: pushNotificationsTypes.CLEAN,
        });
        yield put(notificationActions.showNotification(response.data.message, "error", ["settings"]));
        yield put(replace("/settings"));
    }
}

function* pushNotificationsAskPermission() {
    yield call(pushNotificationsUtils.askPermission);
}

function* listUserDevicesConfigured({ isMobileNative }) {
    try {
        const response = yield call(settings.pushNotificationsListUserDevices);
        if (response && response.type === "I") {
            const listUserDevices = response.data?.data?.listUserDevices;
            const configuredDevices = listUserDevices?.map((dispositivo) => ({
                deviceId: dispositivo.idDevice,
                deviceName: dispositivo.model,
            }));

            yield put({
                type: pushNotificationsTypes.SET_LIST_USER_DEVICES_CONFIGURED,
                configuredDevices,
            });

            if (isMobileNative && listUserDevices.length > 0) {
                const isDeviceConfigured = isCurrentDeviceRegistered(listUserDevices);
                if (isDeviceConfigured) {
                    try {
                        yield call(pushNotificationsUtils.waitForEnabled);
                    } catch {
                        yield put(
                            notificationActions.showNotification(
                                i18nUtils.get("settings.pushNotifications.device.nopermissions.warning"),
                                "warning",
                                ["pushDevices"],
                            ),
                        );
                    }
                }
            }
        } else {
            yield put({
                type: pushNotificationsTypes.CLEAN,
            });
            yield put(notificationActions.showNotification(response.data.message, "error", ["settings"]));
            yield put(replace("/settings"));
        }
    } finally {
        yield put({
            type: pushNotificationsTypes.SET_FETCHING,
            fetching: false,
        });
    }
}

/**
 * Esta funcion chequea si se tienen permisos y/o si el dispositivo esta vinculado
 */
function* checkConfiguration() {
    const isEnabledPlugin = yield call(pushNotificationsUtils.isPushEnabled);
    const enabled = isEnabledPlugin?.enabled;
    const response = yield call(settings.pushNotificationsListUserDevices);
    const isDeviceConfigured = isCurrentDeviceRegistered(response?.data?.data?.listUserDevices);
    yield put({ type: pushNotificationsTypes.CHECK_CONFIGURATION_SUCCESS, enabled, isDeviceConfigured });
}

function* handleRefreshTokenPush({ registrationId }) {
    const user = yield select(sessionSelectors.getUser);
    if (user !== null && user.userId !== null) {
        const exchangeToken = yield select(selectors.getExchangeToken);
        if (exchangeToken !== null) {
            yield call(session.registerUserDevice, exchangeToken, deviceUtils.getDeviceId(), registrationId, null);
        }
    }
}
